import { z } from 'zod';

/**
 * The closed message vocabulary (PRD NFR-006).
 *
 * `ApiError.messageKey` used to be `z.string()` - an OPEN set - and that one
 * type is the root of a whole family of defects, because nothing anywhere could
 * check it:
 *
 *  - a route inventing a key no catalogue carries typechecked, validated and
 *    shipped; the operator dispatching an order whose window had closed got
 *    "Something went wrong." instead of "delivery window is -22 min; the
 *    provider minimum is 20 min", because internal-admin had no `error.provider_limit`;
 *  - `client.ts` falls back to `error.unexpected` for anything it cannot render,
 *    so an unknown key degrades silently at the far end rather than failing;
 *  - the catalogue-completeness suites could check en/fr parity but not the one
 *    thing that matters - that the set of keys the SERVER can send is a subset
 *    of the set each CLIENT can render.
 *
 * This module closes the set. It is the registry, and four things hang off it so
 * it cannot drift:
 *
 *  1. `ErrorMessageKey` narrows `ApiError.messageKey` to a `z.enum`, so an
 *     unregistered key fails contract validation on the wire, at both ends.
 *  2. `apps/api-worker/src/errors.ts` types the `messageKey` parameter of
 *     `apiError()` against it, so a route inventing a key fails the typecheck
 *     rather than reaching a screen untranslated.
 *  3. Each mount's subset (`GUEST_*`, `PARTNER_*`, `BAKERY_*`, `INTERNAL_*`) is what
 *     that portal's catalogue-completeness test iterates, in BOTH locales - so
 *     registering a key without copy fails the build.
 *  4. `messages.test.ts` asserts every mount subset really is a subset of the
 *     platform registry, so the lists cannot fall out of step with each other.
 *
 * A removed key stays registered while any deployed worker can still emit it.
 */

// ---------------------------------------------------------------------------
// Keys every mount can answer with: the endpoint binder and the error mapper.
// ---------------------------------------------------------------------------

/**
 * Emitted by `apps/api-worker/src/bind.ts` (any request can fail validation or
 * idempotency) and by `apps/api-worker/src/errors.ts` (the single place typed
 * domain and service errors become HTTP). Reachable on `/g`, `/c`, `/b` and
 * `/internal` alike, so every portal catalogue must carry all of them.
 */
export const SHARED_ERROR_MESSAGE_KEYS = [
  'error.support_case_stale',
  'error.refund_over_charged',
  'error.support_case_invalid',
  'error.refund_unauthorized',
  'error.refund_order_not_final',
  'error.support_case_not_found',

  // -- endpoint binder (bind.ts) ---------------------------------------------
  'error.validation',
  'error.idempotency_key_required',
  'error.idempotency_conflict',
  'error.internal',
  /*
   * The request budget (index.ts): the data store did not answer in time.
   * Deliberately NOT `error.internal` - this one is temporary and retrying is
   * the right move, and a guest standing in an apartment with a printed code
   * deserves to be told that rather than "Something went wrong."
   */
  'error.temporarily_unavailable',
  // -- error mapper (errors.ts): HTTPException and the domain fallback -------
  'error.http',
  'error.domain',
  'error.cancellation_closed',
  'error.rejection_reason_required',
  /* BAK-003: the counter answered before its offer opened. Acceptance is an
   * interval, and this is its near end refusing - a different refusal from an
   * illegal transition, because the order IS acceptable, just not yet. */
  'error.acceptance_not_open',
  'error.guest_choice_required',
  'error.immediate_only',
  'error.stale_event',
  'error.provider_limit',
  /*
   * RTE-032: the delivery partner accepted a change to a live booking and did
   * not make it. Its own key rather than `error.provider_limit` or a generic
   * conflict, because nothing was refused and nothing the caller sends
   * differently would help - the booking simply is not where every record on
   * this side says it is, and somebody has to go and look at it.
   */
  'error.provider_update_ignored',
  /*
   * RTE-023 (founder decision 2026-09-29): nobody has set the delivery
   * provider, and the platform setting is the only thing that may. Every
   * booking that has not happened yet is refused rather than guessed, and the
   * guest is told delivery is unavailable - not that a provider is failing.
   */
  'error.delivery_unavailable',
  'error.capability_unavailable',
  'error.illegal_transition',
  // -- error mapper: ServiceError codes --------------------------------------
  'error.qr_invalid',
  'error.session_invalid',
  'error.order_not_found',
  /** The order has ended, so the delivery control asked for has nothing to act on. */
  'error.order_ended',
  'error.window_unavailable',
  /* RTE-034 and OPS-025: an immediate window that went behind us between render
   * and confirmation, and the platform selling neither mode. Both are refusals
   * every mount's error mapper can produce, so every catalogue carries them. */
  'error.window_passed',
  /* RTE-027: the courier quoted this delivery above the administered ceiling. */
  'error.courier_quote_above_ceiling',
  /* GST-010: a morning of the stay went while the guest was on the review. */
  'error.stay_morning_unavailable',
  'error.ordering_closed',
  'error.no_bakery',
  'error.choose_shop',
  /*
   * MODE B2 (founder, 2026-09-20): the shop this session was bound to has
   * stopped trading. Neither `error.choose_shop` (there is no chooser in B2)
   * nor `error.no_bakery` (the address is fine): the guest's move is to
   * re-resolve the session, which re-picks the closest shop, and to empty a
   * basket that was filled from a different house's shelf.
   */
  'error.shop_changed',
  'error.minimum_basket',
  'error.payment_declined',
  'error.tip_unsupported',
  'error.tip_window_closed',
  'error.rating_before_delivery',
  'error.order_not_closed',
  'error.not_dispatchable',
  'error.qr_cap',
  'error.import_invalid',
  'error.unknown_entity',
] as const;

/**
 * Keys the CLIENT mints for itself, never received from a server: a request that
 * never reached one, and a response that did not match the contract. They are
 * registered because `ApiRequestError.messageKey` carries them and every screen
 * renders them through the same catalogue lookup as a server key.
 */
export const CLIENT_ERROR_MESSAGE_KEYS = ['error.network', 'error.unexpected'] as const;

// ---------------------------------------------------------------------------
// Per-mount additions (the routes' own refusals)
// ---------------------------------------------------------------------------

/** `apps/api-worker/src/routes/guest/` only. */
export const GUEST_ROUTE_ERROR_MESSAGE_KEYS = [
  'error.code_expired',
  'error.code_invalid',
  'error.tip_failed',
  /*
   * SUP-002: the photograph a guest attaches to an issue report goes through
   * the same store the operator's and the partner's uploads do, and is refused
   * past this deployment's ceiling the same way - 422, because the request is
   * well-formed and the deployment is what cannot hold it.
   *
   * It was registered on `/c` and `/internal` only, which made this the single
   * `apiError` call on a mount that has its own typed helper: `guestError`
   * refused the key, correctly, because the guest registry did not carry it. It
   * reached a guest as a sentence anyway - the PLATFORM catalogue happens to
   * carry copy for it and `errorSentence` falls through there - but the
   * REGISTRY is what guest-web's completeness test iterates, so nothing held
   * that copy in place. Registering it is what makes the loop closed rather
   * than lucky.
   */
  'error.media_too_large',
  'guest.not_servable',
  'guest.service_paused',
] as const;

/** `apps/api-worker/src/routes/partner/` only. */
export const PARTNER_ROUTE_ERROR_MESSAGE_KEYS = [
  'error.address_outside_paris',
  'error.bad_base64',
  'error.batch_not_found',
  'error.geocode_failed',
  'error.import_not_found',
  'error.member_not_found',
  /* A team member's access, changed in the identity provider (CON-007). */
  'error.member_address_unknown',
  'error.member_already_in_team',
  'error.member_last_admin',
  'error.member_address_rejected',
  /*
   * A house's new price would reprice a set of its own range that follows its
   * parts below what the house keeps for that set (#2125 review): the same
   * refusal Internal's catalogue answers.
   */
  'error.guest_price_below_bakery_price',
  /* A house's price typed on a set of its range that follows its parts
     (CAT-069, founder ruling 2026-10-14). */
  'error.price_follows_parts',
  /* A disabled member's request to /c, refused whatever it asks (#2004). */
  'error.member_disabled',
  'error.member_self_disable',
  'error.member_self_remove',
  /* Only a bakery house changes its team on /c (#2160); a property organization reads it. */
  'error.team_read_only',
  'error.team_not_connected',
  'error.team_directory_unavailable',
  'error.payout_not_found',
  'error.properties_not_found',
  'error.property_not_found',
  /* Archiving is terminal (CON-001): no status but ARCHIVED on an archived apartment. */
  'error.apartment_archived',
  'error.qr_cap_reached',
  'error.qr_not_generated',
  /* "Download all QR codes" over a portfolio where no apartment holds one (WEB-019). */
  'error.no_qr_codes',
  /* Bakery organizations on /c (BAK-028, CAT-015). */
  'error.catalogue_item_not_found',
  /* A house retiring a product one of its own PUBLISHED sets names (CAT-019):
     the operations route's refusal, answered on /c too since a mode B shelf
     carries sets. */
  'error.choice_in_published_formule',
  'error.media_too_large',
  /*
   * OPS-036, CON-013 as amended 2026-09-01: a bakery house READS its legal
   * identity here and asks the platform to change it. Its own key rather than
   * `error.capability_unavailable`, because the partner's next move is a
   * message to a person rather than a feature they do not have.
   */
  'error.legal_identity_platform_managed',
  /* The payment step (docs/plans/owner-onboarding.html section 7): a stated
   * tax kind is never taken back to "not stated". */
  'error.tax_kind_locked',
  /* WEB-006: once Stripe has VERIFIED the payout account, nothing it verified
   * changes; a new account instead. Before that everything moves. */
  'error.stripe_identity_locked',
  /* The payment step's own refusals, worded for the owner rather than for an
   * operator sending a link: a fact still missing before Stripe's form, and
   * an organization an operator suspended. */
  'error.payout_details_incomplete',
  'error.organization_suspended',
  'error.payout_provider_unavailable',
  /*
   * OPS-013: an apartment asked onto a menu type its organization is not sold.
   * The owner's form offers only the sold types, so this is a stale form or a
   * hand-written request - and the sentence says whom to ask.
   */
  'error.storefront_mode_not_allowed',
] as const;

/** `apps/api-worker/src/routes/bakery/` only. */
export const BAKERY_ROUTE_ERROR_MESSAGE_KEYS = [
  'error.line_not_found',
  /*
   * BAK-007: the Collected tap arrived before there was a bag to collect. The
   * tap writes an audit row asserting a handover, so it is refused on an order
   * that has not reached Ready and on one that closed without one.
   */
  'error.not_collectable',
  'error.replacement_not_approved',
  'error.replacement_unavailable',
  /*
   * BAK-019, the quantity a counter says it is baking. Above what the guest
   * ordered is a line the platform never sold; above what is LEFT after a
   * reduction is money that has already gone back to the guest, and a card
   * authorization is never increased off-session to fetch it again.
   */
  'error.quantity_above_ordered',
  'error.quantity_already_reduced',
  /*
   * BAK-040, "we need more time". The platform's own refusal: the order is past
   * the point where moving the promise is the counter's to do - a courier is
   * assigned, or a pre-order's job is already booked - and the remedy is to
   * call operations. There is no longer a second one for "you have already had
   * your delay": the delay repeats (2026-08-23, aligned with Deliveroo).
   */
  'error.delay_not_available',
  /*
   * BAK-043, cancelling an accepted order. One refusal and one only: the bag
   * has left the counter, so the decision is no longer the counter's to take
   * (OPS-001). Named rather than folded into a generic conflict, because the
   * remedy is a different one - call operations, not try again.
   */
  'error.cancel_not_available',
  /*
   * The last refusal, and the only one the platform does not decide: an
   * IMMEDIATE order has a courier booked from the moment it was accepted
   * (BAK-004), so its delay is a provider reschedule rather than a refusal -
   * and the provider is entitled to say no once its courier is riding to the
   * pickup. An answer, not a fault, and remedied by calling operations.
   */
  'error.courier_en_route',
  /*
   * The order camera (founder, 2026-10-08). A Ready tap that skipped the
   * photograph on a shop that requires one; a photograph of an order whose
   * bag is not on the shelf or past it; the eleventh photograph of one bag.
   */
  'error.bag_photo_required',
  'error.bag_photo_not_ready',
  'error.bag_photo_limit',
  /* ...and the one an upload of any kind can meet: the picture is over the
     store's ceiling (media.ts), which the counter's downscale should never
     reach. */
  'error.media_too_large',
] as const;

/** `apps/api-worker/src/routes/internal/` only - the mount itself refuses nothing. */
export const INTERNAL_ROUTE_ERROR_MESSAGE_KEYS = [
  'error.alert_not_found',
  'error.bakery_not_found',
  'error.delivery_not_rebookable',
  /*
   * RTE-035: a re-book during a courier search found a rider already on the
   * job it would have cancelled, or the provider refused that cancel. Nothing
   * was changed either way.
   */
  'error.courier_assigned',
  'error.provider_cancel_refused',
  'error.kill_city_required',
  /* "Download all QR codes" on a client where no apartment holds one (WEB-019). */
  'error.no_qr_codes',
  'error.order_line_unknown',
  'error.org_not_found',
  /*
   * An organization's code (numbering-and-tags#org-code): another organization
   * holds it, a delivery provider owns it, or it was already used - named by a
   * closed order's cycle, printed on a document or a payout - and is frozen.
   */
  'error.org_code_taken',
  'error.org_code_reserved',
  'error.org_code_frozen',
  'error.partner_not_found',
  'error.payout_not_found',
  /*
   * The payout approvals (docs/finance/payments-and-payouts.html#approval): each refusal
   * names the operator's next move - release the hold, wait for the balance,
   * tick the first-payout review, complete the legal identity, finish onboarding,
   * record a payout contact.
   */
  'error.payout_held',
  'error.payout_nothing_available',
  'error.payout_identity_incomplete',
  'error.payout_account_not_verified',
  'error.payout_contact_missing',
  'error.payout_bank_details_needed',
  'error.payout_account_restricted',
  'error.payout_account_changed',
  'error.payout_preview_stale',
  /* Finance -> Documents (invoicing section 10): a retry of a document Pennylane
     never receives or already holds, and a reissue the document cannot take. */
  'error.document_not_exportable',
  'error.document_not_reissuable',
  /* Internal's order view (invoicing.html#delivery): Create invoice, then Send. */
  'error.invoice_identity_incomplete',
  'error.invoice_not_created',
  /*
   * Finance -> Delivery statements (reconciliation.html#delivery-statement):
   * the cycle is still open, a match already froze it with other figures, or
   * another entry landed first.
   */
  'error.delivery_statement_open',
  'error.delivery_statement_matched',
  'error.delivery_statement_changed',
  'error.plan_unavailable',
  'error.property_not_found',
  'error.rate_amount_required',
  'error.rate_kind',
  /*
   * A commission has to name the partner who earns it; there is no global one
   * to set. Its own key rather than `error.rate_kind`, because it names a next
   * move: choose a scope.
   */
  'error.rate_scope_required',
  'error.refund_amount_unsupported',
  /*
   * The liability review and the provider claim (PAY-013, PAY-030). Four keys
   * rather than one `error.domain`, because each names a different next move:
   * there is nothing to answer, the amount is missing, the order is not at an
   * ending yet, or the claim is not in a state that action leaves.
   */
  'error.no_open_review',
  'error.decision_amount_required',
  'error.decision_amount_too_large',
  'error.review_not_open_yet',
  'error.claim_action_unavailable',
  /*
   * Three more the same block owes, and each was a 500 with a request id until
   * the worker's map became total over `ServiceErrorCode` (VR2-D6): an answer
   * the review does not offer, a claim named for less than the review holds,
   * and a review two writers grew at the same instant. Each names its own next
   * move, which is why none of them is `error.domain`.
   */
  'error.decision_unavailable',
  'error.partial_claim',
  /*
   * The Reassign control's target validation (OPS-002, finding F-34). Each is
   * its own key because each asks the operator for a different next move: a
   * suspended shop needs reinstating first, and a mode-incompatible house is
   * never a target.
   */
  'error.target_suspended',
  'error.target_mode_incompatible',
  'error.target_other_house',
  /*
   * CAT-001: the target house has agreed no price for something in the basket,
   * so there is no commercial basis on which it could bake it. Its own key
   * because the next move is the operator's and is specific - price the
   * product on that house's range, then move the order.
   */
  'error.target_unpriced',
  /*
   * #2155, decision 7: the target shop has closed the order's day, and no
   * offer is ever written for a closed day.
   */
  'error.target_closed',
  'error.small_order_fee_incomplete',
  /* RTE-023: the delivery adapter chosen in Settings is not one this deploy
   * holds. Registered on this mount as well as the webhook one, because HERE a
   * human reads it and the catalogue answers for the sentence. */
  'error.unknown_provider',
  /* Catalogue administration (CAT-002 to CAT-013). */
  'error.catalogue_item_not_found',
  'error.media_too_large',
  /*
   * Deleting an organization or a bakery location (OPS-003). A record that has
   * ever carried an order is never deleted: the order snapshots its bakery,
   * product names, prices and commission at placement (CAT-006), and payout
   * statements are settled against those snapshots - all of it still readable
   * after the record goes. What is refused is deleting a record that is still
   * HOLDING orders (OPS-003, founder decision 2026-08-30): suspension is what
   * a busy shop gets, and it is deletable once the pipe is empty.
   */
  'error.has_pending_orders',
  /* A mode B location must name the house it belongs to, and it must be one. */
  'error.bakery_org_required',
  /*
   * OPS-036: a link cannot be sent while a required fact is missing. Its own
   * key, and the message NAMES what is missing, because the operator's next
   * move is to type it into the card above the button.
   */
  'error.legal_identity_incomplete',
  /* OPS-036: a house switched off would be onboarded for a trade nobody
   * intends, so the link is refused until it is trading again. */
  'error.house_not_trading',
  /*
   * OPS-036: the payment provider refused or was unreachable when the house's
   * account was to be created. Its own key rather than `error.internal`,
   * because nothing on this side is broken and pressing again is the right
   * move - and the message carries the provider's own sentence, which is what
   * an operator deciding whether to retry or to call somebody needs.
   */
  'error.payout_provider_unavailable',
  'error.bakery_org_mode_mismatch',
  /*
   * Creating or moving a shop geocodes its address, and both refusals are the
   * same two the partner mount already answers - a shop typed in from the wrong
   * arrondissement is the identical mistake whoever is typing. Registering them
   * here rather than folding them into `error.validation` is what lets the
   * screen say which of the two happened.
   */
  'error.address_outside_paris',
  'error.geocode_failed',
  /*
   * A mode B house's own shelf is authored by that house, never by operations
   * (CAT-015). Operations reads it - answering a support call needs that - and
   * is refused the write.
   */
  'error.pool_not_writable',
  /*
   * The kinds a menu is divided into (CAT-016). Each says a different next
   * move, which is why they are four keys and not one refusal.
   */
  'error.product_type_exists',
  'error.product_type_not_found',
  'error.product_type_in_use',
  'error.product_type_built_in',
  'error.product_type_name_required',
  /*
   * The Mode A menu (docs/plans/mode-a-menu-and-pricing.html sections 2 and 7).
   * Each is its own key because each tells the curator a different next move.
   *
   * A house price above the guest price is a NEGATIVE commission - the platform
   * would be paying to sell the product. Refused at the route, and refused
   * again by a database trigger underneath it, so it is impossible from a
   * hand-run script too.
   */
  'error.negative_commission',
  /*
   * The same rule from the other side (#2125 decision
   * guest-price-below-baker-price): a guest price an operator moves below what
   * a baker has agreed to keep. The refusal names every baker in its message,
   * because only the server knows them; it carries no field, and Internal marks
   * the price box by this key.
   */
  'error.guest_price_below_bakery_price',
  /* A house price typed on a set (CAT-048): its products' prices are its price. */
  'error.price_follows_parts',
  /* A set offered to a house that has not priced every product it needs (CAT-048). */
  'error.set_parts_unpriced',
  /* A product agreed with a house with no price (CAT-001). */
  'error.bakery_price_required',
  /* Composition is a thing only a formule has (`kind: MENU`). */
  'error.not_a_formule',
  /* One level only, so the price arithmetic stays finite. */
  'error.formule_in_formule',
  'error.formule_answers',
  /* A choice names a product that is not in the platform pool, or is retired. */
  'error.choice_not_in_pool',
  'error.choice_retired',
  /*
   * RETIRING a product a PUBLISHED set still names (founder decision
   * 2026-08-30, CAT-019). `active: false` takes a product out of the world, so
   * doing it under a set that is on the menu leaves the set pointing at
   * nothing. Unlisting stays allowed and has no key: butter is in every set and
   * sold alone in none, so `listed: false` is the normal state of half of them.
   * The set is named in the refusal's fields.
   */
  'error.choice_in_published_formule',
  /*
   * A SECTION that is not what its kind says it is (CAT-018, founder decision
   * 2026-08-30). Two kinds, two ways to get it wrong: an `INCLUDED` section
   * with nothing under it, and a `CHOICE` section offering one option - a
   * question with one answer, which is what the two kinds exist to stop being
   * written.
   */
  'error.section_empty',
  'error.choice_needs_options',
  /* A formule with no bullets is not a thing to put in front of a guest. */
  'error.formule_without_components',
  /*
   * The product code a create carried already names another product. A code
   * comes from a partner's workbook now (CatalogueItemDraft.productCode), so
   * this is a workbook naming one twice, and it is refused rather than made
   * unique - an invoice line names a product by that string.
   */
  'error.product_code_taken',
  /*
   * The counter fleet's release channel (docs/plans/handheld-fleet.html 2).
   *
   * Every one of these is its own key because every one of them tells the
   * operator a different next move, and the two most important ones are
   * refusals of an ACT rather than of a form: a build that no offline key has
   * signed is not publishable by anybody, and a build most of the fleet was
   * never offered can never become the minimum.
   */
  'error.handheld_release_exists',
  'error.handheld_release_not_found',
  'error.handheld_version_not_above',
  /*
   * The two refusals that are about the MINIMUM rather than about the latest,
   * and they are separate keys because "newer than the one already published"
   * is the wrong sentence for both. One says the minimum can only go up; the
   * other says the only build that can become the minimum is the one the fleet
   * is currently being offered - which is what keeps the 100 % gate a statement
   * about THAT release rather than about whatever was widened last.
   */
  'error.handheld_version_below_minimum',
  'error.handheld_not_latest',
  'error.handheld_release_unsigned',
  'error.handheld_release_halted',
  'error.handheld_release_not_published',
  'error.handheld_manifest_mismatch',
  'error.handheld_manifest_channel',
  'error.handheld_signature_invalid',
  'error.handheld_rollout_incomplete',
  'error.handheld_confirmation_mismatch',
  'error.handheld_location_unknown',
  /* A threshold PAIR that would clear on the beat it raises (plan 10). */
  'error.handheld_threshold_pair',
  /*
   * The device page and everything addressed at one counter (plan 5, 11). ONE
   * key, because a removed device and a mistyped id are now the same next move:
   * removal DELETES the row (BAK-037), so there is no such thing as a device
   * that exists and answers nothing. The second key this carried said exactly
   * that and could no longer be reached.
   */
  'error.handheld_device_not_found',
  /*
   * The rollout (plan 12). Three keys because three different things are
   * wrong: nothing is published, so there is no rollout to widen; the number
   * typed is below the one already reached, and a rollout only ever widens;
   * and an un-halt was asked of a build nobody halted.
   */
  'error.handheld_nothing_published',
  'error.handheld_rollout_not_widening',
  'error.handheld_release_not_halted',
  /* A diagnostic object this device never left behind (plan 13). */
  'error.handheld_diagnostic_not_found',
  /*
   * Reading a scenario run's evidence out of the RUNS bucket (OPS-038). The
   * first is a DEPLOYMENT fault rather than an empty screen: an operator told
   * "no runs yet" has no way to tell that apart from a bucket nothing ever
   * published to.
   */
  'error.run_evidence_unavailable',
  'error.run_not_found',
  'error.run_shot_not_found',
  'error.whatsapp_not_configured',
  'error.whatsapp_unreachable',
  /* WEB-006: a property partner's verified identity, once its payout account exists. */
  'error.stripe_identity_locked',
] as const;

/**
 * `apps/api-worker/src/routes/site.ts` only - the public marketing site's forms
 * (WEB-025, WEB-026). These are human-facing, so `packages/i18n` carries copy
 * for them in both locales like every other key here; `site.js` repeats those
 * two strings rather than importing the catalogue, because the static pages
 * have no build step and no module system to import it with.
 */
export const SITE_ROUTE_ERROR_MESSAGE_KEYS = [
  'error.bot_check_failed',
  'error.bot_check_unavailable',
] as const;

/**
 * `apps/api-worker/src/routes/webhooks.ts` only. No human ever reads these - a
 * provider does - so they are registered for wire validation but no catalogue
 * is held to them.
 */
export const WEBHOOK_ROUTE_ERROR_MESSAGE_KEYS = [
  'error.bad_json',
  'error.bad_signature',
  'error.unknown_provider',
  'error.inbound_sms_deferred',
] as const;

// ---------------------------------------------------------------------------
// What each portal must be able to render
// ---------------------------------------------------------------------------

/** Everything a guest's browser can receive (PRD GST-*). */
export const GUEST_ERROR_MESSAGE_KEYS = [
  ...SHARED_ERROR_MESSAGE_KEYS,
  ...GUEST_ROUTE_ERROR_MESSAGE_KEYS,
  ...CLIENT_ERROR_MESSAGE_KEYS,
] as const;

/** Everything a partner's dashboard can receive (PRD CON-*). */
export const PARTNER_ERROR_MESSAGE_KEYS = [
  ...SHARED_ERROR_MESSAGE_KEYS,
  ...PARTNER_ROUTE_ERROR_MESSAGE_KEYS,
  ...CLIENT_ERROR_MESSAGE_KEYS,
] as const;

/** Everything a bakery tablet can receive (PRD BAK-*). */
export const BAKERY_ERROR_MESSAGE_KEYS = [
  ...SHARED_ERROR_MESSAGE_KEYS,
  ...BAKERY_ROUTE_ERROR_MESSAGE_KEYS,
  ...CLIENT_ERROR_MESSAGE_KEYS,
] as const;

/** Everything an operator's console can receive (PRD INTERNAL-*). */
export const INTERNAL_ERROR_MESSAGE_KEYS = [
  ...SHARED_ERROR_MESSAGE_KEYS,
  ...INTERNAL_ROUTE_ERROR_MESSAGE_KEYS,
  ...CLIENT_ERROR_MESSAGE_KEYS,
] as const;

/**
 * The whole vocabulary. Duplicates across mounts (`error.property_not_found` is
 * emitted by both `/c` and `/internal`) are harmless: the enum keys by value and the
 * inferred type is a union.
 */
export const ERROR_MESSAGE_KEYS = [
  ...SHARED_ERROR_MESSAGE_KEYS,
  ...GUEST_ROUTE_ERROR_MESSAGE_KEYS,
  ...PARTNER_ROUTE_ERROR_MESSAGE_KEYS,
  ...BAKERY_ROUTE_ERROR_MESSAGE_KEYS,
  ...INTERNAL_ROUTE_ERROR_MESSAGE_KEYS,
  ...WEBHOOK_ROUTE_ERROR_MESSAGE_KEYS,
  ...SITE_ROUTE_ERROR_MESSAGE_KEYS,
  ...CLIENT_ERROR_MESSAGE_KEYS,
] as const;

/** A key the platform may put on the wire. Anything else fails validation. */
export type ErrorMessageKey = (typeof ERROR_MESSAGE_KEYS)[number];

export type GuestErrorMessageKey = (typeof GUEST_ERROR_MESSAGE_KEYS)[number];
export type PartnerErrorMessageKey = (typeof PARTNER_ERROR_MESSAGE_KEYS)[number];
export type BakeryErrorMessageKey = (typeof BAKERY_ERROR_MESSAGE_KEYS)[number];
export type InternalErrorMessageKey = (typeof INTERNAL_ERROR_MESSAGE_KEYS)[number];

/** The schema `ApiError.messageKey` uses. An unknown key is a validation error. */
export const ErrorMessageKeySchema = z.enum(ERROR_MESSAGE_KEYS);

/**
 * Registered keys that say nothing the HTTP status has not already said.
 *
 * `error.http` is what a bare framework `HTTPException` serialises as - it means
 * "a status with no domain meaning behind it", and it is emitted with statuses
 * as different as 401 and 404. Rendering its copy verbatim is how an owner
 * opening a deleted apartment was told to "correct the highlighted fields":
 * status 404, code `bad_request`, messageKey `error.http`. `error.domain` and
 * `error.internal` are the same shape - the fallbacks of the error mapper.
 *
 * A renderer must skip its catalogue for these and describe the STATUS instead.
 * Every other registered key is more specific than the status by construction,
 * so it wins.
 */
export const STATUS_FIRST_ERROR_MESSAGE_KEYS = [
  'error.http',
  'error.domain',
  'error.internal',
] as const;

const STATUS_FIRST = new Set<string>(STATUS_FIRST_ERROR_MESSAGE_KEYS);

/** True when the HTTP status describes the failure better than the key does. */
export function isStatusFirstErrorKey(key: string): boolean {
  return STATUS_FIRST.has(key);
}

/**
 * Narrows an arbitrary string to a registered key, or `null`.
 *
 * The typed client uses this rather than trusting the body: a server ahead of
 * this client (a key registered after the app was built) must degrade to
 * `error.unexpected` and still render a sentence, not put a raw key on screen.
 */
export function asErrorMessageKey(value: unknown): ErrorMessageKey | null {
  const parsed = ErrorMessageKeySchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

// ---------------------------------------------------------------------------
// The guest timeline vocabulary (PRD GST-036)
// ---------------------------------------------------------------------------

/**
 * `StageEntry.messageKey` was `z.string()` too, and the worker composes it as
 * `guest.stage.${stage.toLowerCase()}`. Deriving the enum from the same stage
 * list the wire enum uses is what stops the composed string and the catalogue
 * drifting apart without anything failing.
 */
export const GUEST_STAGE_MESSAGE_KEYS = [
  'guest.stage.order_placed',
  'guest.stage.order_confirmed',
  'guest.stage.preparing',
  'guest.stage.order_ready',
  'guest.stage.finding_courier',
  'guest.stage.courier_assigned',
  'guest.stage.on_the_way',
  'guest.stage.arriving',
  'guest.stage.delivered',
] as const;

export type GuestStageMessageKey = (typeof GUEST_STAGE_MESSAGE_KEYS)[number];

export const GuestStageMessageKeySchema = z.enum(GUEST_STAGE_MESSAGE_KEYS);

/** The one place the composed key is built. Never re-derived at a call site. */
export function guestStageMessageKey(stage: string): GuestStageMessageKey {
  const key = `guest.stage.${stage.toLowerCase()}`;
  const parsed = GuestStageMessageKeySchema.safeParse(key);
  if (!parsed.success) throw new Error(`Unknown guest stage: ${stage}`);
  return parsed.data;
}
